import "@/styles/globals.css";
import "@/styles/iconfont.css";
import Script from "next/script";
import { v4 as uuidv4 } from "uuid";

import type { AppProps, AppContext } from "next/app";
import { appWithTranslation, i18n } from "next-i18next";
import Layout from "@/components/Layout";
import { Mulish, Roboto } from "next/font/google";
import { AnimatePresence } from "framer-motion";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import datafluxRumInit from "@/utils/guance";
import { initClientHeaders, setServerQuery } from "@/utils/headers";
import NextApp from "next/app";
import { ConfigProvider } from "react-vant";
// import { SessionProvider } from "next-auth/react";
import { LoginProvider } from "@/components/LoginProvider";
import { SystemBrowserPopupProvider } from "@/components/common/SystemBrowserPopup/Provider";
// import { GoogleOAuthProvider } from "@/components/google/dist/index.js";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { getLang } from "@/utils/getLang";
import { setGlobalHost } from "@/utils/globalState";
import Head from "next/head";
import { AppBarInfoContext } from "@/context/AppBarInfoContext";
import { nativeMethod } from "@/utils/bridge";
import { preloadAudioEqualizerAnimation } from "@/components/function/common/AudioEqualizerIcon";
import {
  applyThemeFromPayload,
  initializeThemeRuntime,
  syncThemeRuntimeForRoute,
} from "@/theme/runtime";

import { shouldPersistArtworkShareTheme } from "@/utils/ai/artworkShareTheme";

// 声明window.__vconsole__
declare global {
  interface Window {
    __vconsole__?: () => void;
  }
}

const mulish = Mulish({
  subsets: ["latin"],
  weight: ["400", "500", "700", "900", "1000"],
  display: "swap",
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const roboto = Roboto({
  subsets: ["latin"],
  weight: ["400", "500", "700", "900"],
  display: "swap",
});

const h5ApiPreconnectUrl =
  process.env.NEXT_PUBLIC_BASE_URL ||
  (process.env.NEXT_PUBLIC_ENV === "prod"
    ? "https://h5-api.buscari.com"
    : "https://test-h5-api.buscari.com");

function App({
  Component,
  pageProps: { session, faviconUrl, ...pageProps },
}: AppProps) {
  const router = useRouter();
  const [appBarInfo, setAppBarInfo] = useState<{
    [key: string]: string;
  } | null>(null);
  useEffect(
    () =>
      initializeThemeRuntime({
        shouldPersist: () => shouldPersistArtworkShareTheme(window),
      }),
    []
  );
  useEffect(() => {
    const handleRouteChange = () => syncThemeRuntimeForRoute();
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => router.events.off("routeChangeComplete", handleRouteChange);
  }, [router.events]);
  function getOrCreateSessionId() {
    if (typeof window === "undefined") return "";
    let sessionId = sessionStorage.getItem("session_id");
    console.log("sessionId", sessionId);
    if (!sessionId) {
      // 生成唯一ID
      sessionId = uuidv4();

      sessionStorage.setItem("session_id", sessionId);
    }
    return sessionId;
  }
  // 在客户端初始化 headers
  useEffect(() => {
    // 客户端最早调用的时机
    try {
      nativeMethod.getAppbar().then((res) => {
        setAppBarInfo(res || {});
        applyThemeFromPayload(res);
        setTimeout(() => {
          console.log("getAppbar======", res);
        }, 10000);
      });
    } catch (error) {
      console.error("访问 window.quan_function 失败:", error);
    }

    // 客户端最早执行的代码
    // 预加载音频均衡器动画，避免首次渲染时 loading
    preloadAudioEqualizerAnimation();

    // 可以在这里添加任何需要在客户端最早执行的代码
    // 例如：初始化全局变量、检查环境、设置事件监听器等

    initClientHeaders();
    getOrCreateSessionId();
    // initSafeArea();

    // 在客户端导入触摸模拟器
    if (typeof window !== "undefined") {
      import("@vant/touch-emulator");
    }
  }, []);
  const { statusBarHeight, safeAreaBottom } = appBarInfo || {};
  useEffect(
    () => {
      console.log("appBarInfo", appBarInfo);
      if (typeof window === "undefined") return;
      const h = Number(statusBarHeight) || 0;
      const safe_b = Number(safeAreaBottom) || 0;
      // 注入全局 CSS 变量
      document.documentElement.style.setProperty(
        "--status-bar-height",
        `${h}px`
      );
      document.documentElement.style.setProperty(
        "--safe-area-bottom",
        `${safe_b}px`
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusBarHeight]
  );

  useEffect(
    () => {
      // 只在客户端执行
      if (typeof window !== "undefined" && i18n) {
        // 检查是否已经被 middleware 处理
        // middleware 处理条件：路径包含 /in/ 且 query 中有 language 参数
        const isMiddlewareHandled =
          router.pathname.includes("/in/") && router.query.language;

        // 如果已经被 middleware 处理，则跳过 router.replace
        if (isMiddlewareHandled) {
          // 阿语页面仍然要处理
          console.log(
            "[_app.tsx] 页面已被 middleware 处理，跳过 router.replace"
          );
          // 站内页面已经被middleware处理过，此处不需要router.replace
          return;
        }
        if (
          (Component as typeof Component & { preserveServerLocale?: boolean })
            .preserveServerLocale
        ) {
          return;
        }
        // 支持的语言
        const supportLangs = ["en", "zh", "es", "pt", "ar", "tr"]; //项目原有的中文是zh  客户端可能会有zh_CN
        // 当前 i18n 语言
        const currentLang = i18n.language;
        // 获取浏览器语言
        // const browserLang = navigator.language?.toLowerCase() || "en";
        let lang = "en";
        const clientLang = getLang();
        if (clientLang.startsWith("zh")) {
          lang = "zh_CN";
        } else if (clientLang.startsWith("es")) {
          lang = "es";
        } else if (clientLang.startsWith("pt")) {
          lang = "pt";
        } else if (clientLang.startsWith("ar")) {
          lang = "ar";
        } else if (clientLang.startsWith("tr")) {
          lang = "tr";
        }
        router.replace(router.asPath, undefined, { locale: lang });
        // 如果当前语言不是支持的语言，或者是默认语言，则切换
        if (
          (!supportLangs.includes(currentLang) || currentLang === "en") &&
          supportLangs.includes(lang) &&
          currentLang !== lang
        ) {
          i18n.changeLanguage(lang);
        }
        // 文章页不需要重写路由
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // 根据语言动态设置 dir 属性
  useEffect(() => {
    const updateDir = () => {
      // 优先使用 i18n.language，因为它会立即反映语言变化
      // 如果 i18n.language 不存在，则使用 router.locale 或 getLang() 作为后备
      // ? 因为 router.locale有默认值，所以先取getLang(router?.query)
      const currentLang = (i18n?.language ||
        getLang() ||
        router?.locale ||
        "en") as string;
      console.log("currentLang=====", currentLang);

      const dir = currentLang === "ar" ? "rtl" : "ltr";
      document?.documentElement?.setAttribute("dir", dir);
      document?.documentElement?.setAttribute("lang", currentLang);
    };

    if (typeof window === "undefined") {
      return;
    }

    // 初始设置
    updateDir();

    // 如果 i18n 存在，监听语言变化事件
    if (i18n) {
      i18n.on("languageChanged", updateDir);
    }

    // 清理函数：移除事件监听器
    return () => {
      if (i18n) {
        i18n.off("languageChanged", updateDir);
      }
    };
  }, [router.locale]);

  useEffect(() => {
    if (!window.google) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    // 非生产环境引入vConsole
    if (
      typeof window !== "undefined" &&
      process.env.NEXT_PUBLIC_ENV !== "prod"
    ) {
      import("vconsole").then((VConsole) => {
        // 避免重复实例化
        if (!window.__vconsole__) {
          window.__vconsole__ = new VConsole.default();
        }
      });
    }
    // 告知app页面加载成功 -用于文件助手对话
    nativeMethod.pageLoadSuccess();
  }, []);
  useEffect(() => {
    // 初始化观测云 RUM
    datafluxRumInit();
  }, []);

  return (
    <AppBarInfoContext.Provider value={appBarInfo}>
      <Head>
        <title>SeeKee</title>
        <meta name="description" content="SeeKee" />
        <meta name="keywords" content="SeeKee seekee seekeeai browser ai" />
        <meta name="og:image" content={faviconUrl} />
        <link href={faviconUrl || ""} type="image/x-icon" rel="icon" />
        <link
          rel="preconnect"
          href={h5ApiPreconnectUrl}
          crossOrigin="anonymous"
        />
        <link
          rel="preconnect"
          href="https://news-img.buscari.com"
          crossOrigin="anonymous"
        />

        <script
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: `
                (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "rjqhke7zmf");
              `,
          }}
        />
      </Head>
      {/* 加载 Google Identity Services */}
      {/* <Script
        src="https://accounts.google.com/gsi/client"
        strategy="afterInteractive"
      /> */}
      {/* 加载 Google Identity Services */}
      <Script
        src="https://accounts.google.com/gsi/client"
        strategy="afterInteractive" // 在页面交互后加载
      />
      {/* <SessionProvider session={session}> */}
      <GoogleOAuthProvider
        clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID as string}
      >
        <ConfigProvider
          themeVars={{
            primaryColor: "var(--theme-primary)",
            textColor: "var(--theme-text-primary)",
            textColor2: "var(--theme-text-secondary)",
            background: "var(--theme-page-bg)",
            background2: "var(--theme-surface)",
            borderColor: "var(--theme-border)",
            activeColor: "var(--theme-surface-muted)",
            mask: "var(--theme-overlay)",
          }}
        >
          <LoginProvider>
            <SystemBrowserPopupProvider>
              <main className={mulish.className + " h-full"}>
                <Layout>
                  <AnimatePresence
                    mode="wait"
                    initial={false}
                    onExitComplete={() => window.scrollTo(0, 0)}
                  >
                    <Component {...pageProps} key={router.route} />
                  </AnimatePresence>
                </Layout>
              </main>
            </SystemBrowserPopupProvider>
          </LoginProvider>
        </ConfigProvider>
      </GoogleOAuthProvider>
      {/* </SessionProvider> */}
    </AppBarInfoContext.Provider>
  );
}

// 获取初始属性
App.getInitialProps = async (appContext: AppContext) => {
  // 调用父类的 getInitialProps
  const host = appContext.ctx?.req?.headers.host || "";
  // 设置全局 host
  setGlobalHost(host);
  const appProps = await NextApp.getInitialProps(appContext);
  // 设置服务端查询参数
  if (appContext.ctx.req) {
    setServerQuery(appContext.ctx.query);
  }

  // 生成 favicon URL
  const protocol = appContext.ctx?.req?.headers["x-forwarded-proto"] || "http";
  const faviconUrl = `${protocol}://${host}/favicon.ico`;

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      faviconUrl,
    },
  };
};

export default appWithTranslation(App);
